// AppShell + ProductTopBar + 9-pt sub-nav per handoff §2.
//
// Шаг 2 (roadmap ai-agents-new-frontend): навигация теперь **URL-driven** через
// react-router. `useLocation()` → активный пункт sub-nav (`resolveActiveNav`);
// `useNavigate()` → клик по nav-item меняет URL; `<AppRoutes>` рендерит контент
// по pathname. Это даёт deep-link на 13 маршрутов из handoff `01 §3` и кнопку
// «назад» через History API без своей реализации.
//
// Что предоставляет каркас (Шаг 2 acceptance):
//   - AppShell (workbench, aurora-nocturne)
//   - ProductTopBar (FrmLogo + AppSwitcher всех 4 production-сервисов + поиск ⌘K
//     + Обновить + Уведомления + Пользователь из useUser())
//   - 9-pt sub-nav, активный пункт подсвечивается по URL
//   - ToastHost для уведомлений (включая InProgressAction → IN_PROGRESS_REGISTRY)
//   - `<AppRoutes>` со всеми 13 маршрутами + 404 fallback

import { useCallback, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import {
  AppShell,
  FRM_PRODUCTION_APPS,
  NotificationDrawer,
  ProductTopBar,
  ToastHost,
  WorkbenchStatusBar,
  useUser,
  type AppShellContentPreset,
  type ProductTopBarNavItem,
} from '@frm/ui'

import { NAV_ITEMS, resolveActiveNav, type NavId } from './nav'
import { AppRoutes } from './routes'

const SEARCH_PLACEHOLDER: Record<NavId, string> = {
  agents: 'Поиск агентов и разделов',
  skills: 'Поиск скилов',
  projects: 'Поиск проектов и спринтов',
  sessions: 'Поиск сессий',
  releases: 'Поиск релизов и функций',
  chat: 'Поиск по диалогу',
  vaults: 'Поиск файлов хранилища',
  help: 'Поиск по справке',
}

// AppSwitcher: handoff §2.1 — current=team, остальные 3 — live.
// Источник `FRM_PRODUCTION_APPS` из `@frm/ui` уже содержит все 4 с правильными abbr;
// маркируем team как current для текущего сервиса.
const APP_SWITCHER_APPS = FRM_PRODUCTION_APPS.map((app) => ({
  ...app,
  current: app.id === 'team',
}))

function resolveContentPreset(pathname: string): AppShellContentPreset {
  if (pathname === '/' || pathname === '') return 'wide'
  if (pathname.startsWith('/agents/')) return 'wide'
  if (pathname === '/skills') return 'wide'
  if (pathname === '/projects' || pathname.startsWith('/projects/')) return 'wide'
  if (pathname === '/sessions') return 'wide'
  if (pathname === '/releases') return 'wide'
  if (pathname === '/chat' || pathname.startsWith('/chat/')) return 'wide'
  if (pathname === '/vaults' || pathname.startsWith('/vaults/')) return 'wide'
  if (pathname === '/help') return 'wide'
  return 'standard'
}

export function App() {
  const location = useLocation()
  const navigate = useNavigate()
  const activeNav = useMemo(() => resolveActiveNav(location.pathname), [location.pathname])
  const contentPreset = useMemo(() => resolveContentPreset(location.pathname), [location.pathname])
  const queryClient = useQueryClient()
  const isFetching = useIsFetching()
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  // useUser() — AuthGuard уже подтвердил статус authenticated до рендера App,
  // поэтому здесь user приходит из того же `/api/auth/me` без редиректов.
  // Дубликат запроса — допустимая цена за stateless-хук (см. AuthProvider TBD v0.2).
  const { user, logout } = useUser()

  const handleNavSelect = useCallback(
    (item: ProductTopBarNavItem) => {
      const nav = NAV_ITEMS.find((entry) => entry.id === item.id)
      if (nav) {
        navigate(nav.href)
      }
    },
    [navigate],
  )

  const handleRefresh = useCallback(async () => {
    await queryClient.invalidateQueries()
  }, [queryClient])

  const handleLogout = useCallback(() => {
    void logout()
  }, [logout])

  return (
    <AppShell
      mode="workbench"
      padding="none"
      topBar={
        <ProductTopBar
          productLabel="Команда"
          product="team"
          logoAnimated
          apps={APP_SWITCHER_APPS}
          navItems={NAV_ITEMS}
          navPlacement="subnav"
          activeNav={activeNav}
          onNavSelect={handleNavSelect}
          searchPlaceholder={SEARCH_PLACEHOLDER[activeNav]}
          onRefresh={handleRefresh}
          refreshing={isFetching > 0}
          refreshLabel="Обновить"
          refreshingLabel="Обновляем"
          notificationCount={0}
          onNotificationsClick={() => setNotificationsOpen(true)}
          user={user}
          onLogout={handleLogout}
        />
      }
      footer={<WorkbenchStatusBar connectionSlot="owner web" versionSlot="@frm/ui 1.12.0" roleSlot="owner" />}
      toast={<ToastHost position="bottom-right" />}
      contentPreset={contentPreset}
    >
      <AppRoutes />
      <NotificationDrawer
        open={notificationsOpen}
        items={[]}
        description="Источник уведомлений ещё не подключён к backend API."
        onClose={() => setNotificationsOpen(false)}
      />
    </AppShell>
  )
}
